import { useState } from "react";
import { actions, money, useRateCard, STUDIO, defaultEngagement } from "../lib/store";
import { computeEstimate, defaultConfig } from "../lib/rates";
import { useCan } from "../lib/auth";
import { StatusBadge, Modal, Avatar } from "../components/ui";
import { Icon } from "../components/Icons";
import { SignaturePad } from "../components/SignaturePad";
import { openWhatsApp } from "../lib/whatsapp";
import { fmtDate } from "../lib/format";
import { engagementDoc } from "../lib/pdf";
import { PdfPreview } from "../components/PdfPreview";
import type { Project } from "../lib/types";
import { RecordBadge } from "../components/RecordStatus";

export default function EngagementDoc({ p, mode }: { p: Project; mode: "studio" | "client" }) {
  const rc = useRateCard();
  const canEdit = useCan("brief") && mode === "studio";
  const eng = p.engagement ?? defaultEngagement();
  const config = p.estimate ?? defaultConfig(p.budget, p.currency);
  const est = computeEstimate(config, rc);
  const ccy = config.currency;
  const [signOpen, setSignOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [sig, setSig] = useState<string | null>(null);
  const [signName, setSignName] = useState(p.client.name);
  const [agree, setAgree] = useState(false);

  const shareText = `${STUDIO.name} — Letter of Engagement for ${p.name}. Please review and sign in your portal: https://${STUDIO.domain}/portal/${p.id}/engagement (access code from your invitation).`;
  function sign() { if (!signName.trim() || !agree) return; actions.signEngagement(p.id, signName.trim(), sig || ""); setSignOpen(false); }

  return (
    <div className="reveal">
      <div className="card pad" style={{ marginBottom: 18 }}>
        {/* header */}
        <div className="row between wrap" style={{ gap: 12, borderBottom: "1px solid var(--line)", paddingBottom: 16, marginBottom: 16 }}>
          <div className="row" style={{ gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center" }}><Icon.logo width={22} /></div>
            <div><div className="serif" style={{ fontSize: 20 }}>{STUDIO.name}</div><div className="kicker" style={{ fontSize: 9.5 }}>Letter of Engagement</div></div>
          </div>
          <div style={{ textAlign: "right" }}><StatusBadge status={eng.status} /><div className="dim mono" style={{ fontSize: 11.5, marginTop: 4 }}>{p.code}</div></div>
        </div>

        {/* appointment */}
        <p style={{ fontSize: 15, lineHeight: 1.7 }}>
          This letter confirms the appointment of <b>{STUDIO.name}</b> ({STUDIO.principal}, Principal) by <b>{p.client.name}</b>{p.client.company ? ` of ${p.client.company}` : ""} for the provision of professional design services on <b>{p.name}</b> — {p.type}{p.location ? `, ${p.location}` : ""}{p.area ? `, ${p.area}` : ""}.
        </p>

        {/* fee & schedule */}
        <div className="kicker" style={{ margin: "22px 0 10px" }}>Professional fee</div>
        <div className="row between" style={{ marginBottom: 12 }}>
          <span className="muted">Total design fee ({(est.pctOfBudget * 100).toFixed(1)}% of {money(config.budget, ccy)} construction budget)</span>
          <span className="serif" style={{ fontSize: 22 }}>{money(est.total, ccy)}</span>
        </div>
        <div className="panel" style={{ padding: "6px 14px" }}>
          {est.phases.map((ph, i) => (
            <div key={ph.key} className="row between" style={{ padding: "8px 0", borderTop: i ? "1px solid var(--line)" : "none", fontSize: 13.5 }}>
              <span>{ph.label} <span className="dim">· {ph.pctLabel}%</span></span>
              <span className="mono">{money(ph.amount, ccy)}</span>
            </div>
          ))}
        </div>
        <div className="dim" style={{ fontSize: 12, marginTop: 8 }}>Fees payable stage by stage per the schedule above. A detailed fee proposal accompanies this letter.</div>

        {/* additional services — out of scope */}
        {(rc.services ?? []).length > 0 && <>
          <div className="kicker" style={{ margin: "24px 0 4px" }}>Additional services &amp; charges</div>
          <div className="dim" style={{ fontSize: 12, marginBottom: 10 }}>Outside the agreed scope — billed per occurrence, as and when instructed. Not included in the fee above.</div>
          <div className="panel" style={{ padding: "4px 14px" }}>
            {(rc.services ?? []).map((s, i) => (
              <div key={s.key} style={{ padding: "9px 0", borderTop: i ? "1px solid var(--line)" : "none" }}>
                <div className="row between" style={{ gap: 12, alignItems: "baseline" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 500 }}>{s.label}</span>
                  <span className="mono" style={{ fontSize: 13.5, whiteSpace: "nowrap" }}>{money(s.rate, ccy)} <span className="dim">/ {s.unit}</span></span>
                </div>
                {s.blurb && <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>{s.blurb}{s.included ? ` · first ${s.included} included in the fee` : ""}</div>}
              </div>
            ))}
          </div>
        </>}

        {/* limitations */}
        <div className="row between" style={{ margin: "24px 0 10px" }}>
          <div className="kicker">Limitations of services</div>
          {canEdit && <button className="chip" style={{ fontSize: 11.5 }} onClick={() => actions.updateLimitations(p.id, [...eng.limitations, "New limitation…"])}><Icon.plus width={12} />Add</button>}
        </div>
        <div className="col" style={{ gap: 8 }}>
          {eng.limitations.map((l, i) => (
            <div key={i} className="row" style={{ gap: 10, alignItems: "flex-start" }}>
              <span style={{ color: "var(--ink-3)", marginTop: 2, flexShrink: 0 }}>—</span>
              {canEdit
                ? <><input className="input" style={{ padding: "6px 10px", fontSize: 13 }} value={l} onChange={(e) => actions.updateLimitations(p.id, eng.limitations.map((x, j) => j === i ? e.target.value : x))} /><button className="iconbtn" style={{ width: 28, height: 28, flexShrink: 0 }} onClick={() => actions.updateLimitations(p.id, eng.limitations.filter((_, j) => j !== i))}><Icon.close width={13} /></button></>
                : <span style={{ fontSize: 14, lineHeight: 1.5 }}>{l}</span>}
            </div>
          ))}
        </div>

        {/* terms */}
        <div className="kicker" style={{ margin: "24px 0 10px" }}>Terms of appointment</div>
        {canEdit
          ? <textarea className="textarea" value={eng.terms} onChange={(e) => actions.updateEngagementTerms(p.id, e.target.value)} style={{ minHeight: 110 }} />
          : <p className="muted" style={{ fontSize: 14, lineHeight: 1.65 }}>{eng.terms}</p>}

        {/* signature block */}
        <div className="kicker" style={{ margin: "26px 0 12px" }}>Acceptance</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }} className="sign-grid">
          <div>
            <div style={{ height: 60, borderBottom: "1px solid var(--ink-2)", display: "flex", alignItems: "flex-end", paddingBottom: 4 }}>
              {eng.status === "signed" && eng.signatureDataUri ? <img src={eng.signatureDataUri} alt="signature" style={{ maxHeight: 56, maxWidth: "100%" }} /> : <span className="dim serif" style={{ fontSize: 15 }}>{eng.status === "signed" ? eng.signedBy : ""}</span>}
            </div>
            <div style={{ fontSize: 12.5, marginTop: 6 }}><b>{eng.signedBy || p.client.name}</b> · Client</div>
            <div className="dim" style={{ fontSize: 11.5 }}>{eng.status === "signed" ? `Signed ${fmtDate(eng.signedOn)}` : "Awaiting signature"}</div>
          </div>
          <div>
            <div style={{ height: 60, borderBottom: "1px solid var(--ink-2)", display: "flex", alignItems: "flex-end", paddingBottom: 4 }}><span className="serif" style={{ fontSize: 18, fontStyle: "italic" }}>{STUDIO.principal}</span></div>
            <div style={{ fontSize: 12.5, marginTop: 6 }}><b>{STUDIO.principal}</b> · {STUDIO.name}</div>
            <div className="dim" style={{ fontSize: 11.5 }}>{eng.issuedOn ? `Issued ${fmtDate(eng.issuedOn)}` : "Draft"}</div>
          </div>
        </div>

        {/* actions */}
        <div className="row between wrap" style={{ gap: 10, marginTop: 22, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn ghost sm" onClick={() => setPdfOpen(true)}><Icon.report width={14} />Preview PDF</button>
            <button className="btn ghost sm" style={{ color: "#25D366", borderColor: "#25D366" }} onClick={() => openWhatsApp(shareText)}><Icon.whatsapp width={15} />Share</button>
          </div>
          <div className="row" style={{ gap: 8 }}>
            {mode === "studio" && canEdit && eng.status === "draft" && <button className="btn accent sm" onClick={() => actions.issueEngagement(p.id)}><Icon.send width={14} />Issue for signature</button>}
            {mode === "studio" && eng.status === "issued" && <span className="badge info"><span className="dot" />Awaiting client signature</span>}
            {mode === "studio" && eng.status === "issued" && <RecordBadge p={p} kind="engagement-issued" title="Letter of engagement — issued for signature" by={STUDIO.principal} compact />}
            {/* Signing isn't held behind the studio remembering to press "issue" —
                the client can accept whatever they're looking at, unless it's
                already signed. */}
            {mode === "client" && eng.status !== "signed" && <button className="btn accent" onClick={() => setSignOpen(true)}><Icon.sign width={16} />Review &amp; sign</button>}
            {eng.status === "signed" && <span className="badge ok"><Icon.check width={13} />Signed by {eng.signedBy}</span>}
            {eng.status === "signed" && <RecordBadge p={p} kind="engagement-signed" title="Letter of engagement — signed" by={eng.signedBy ?? p.client.name} compact />}
          </div>
        </div>
      </div>

      <Modal open={signOpen} onClose={() => setSignOpen(false)} title="Sign your engagement" wide>
        <div className="col" style={{ gap: 14 }}>
          <p className="muted" style={{ fontSize: 14 }}>By signing you accept the appointment of {STUDIO.name}, the fee of <b>{money(est.total, ccy)}</b>, and the scope and limitations set out above.</p>
          <div className="field"><label>Full name</label><input className="input" value={signName} onChange={(e) => setSignName(e.target.value)} /></div>
          <div className="field"><label>Signature</label><SignaturePad onChange={setSig} /></div>
          <label className="row" style={{ gap: 10, cursor: "pointer", fontSize: 13.5 }}>
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--accent)" }} />
            <span>I have read and accept this letter of engagement and its limitations of services.</span>
          </label>
          <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
            <button className="btn ghost" onClick={() => setSignOpen(false)}>Cancel</button>
            <button className="btn accent" onClick={sign} disabled={!signName.trim() || !agree}><Icon.sign width={15} />Sign &amp; accept</button>
          </div>
        </div>
      </Modal>
      <PdfPreview open={pdfOpen} onClose={() => setPdfOpen(false)} title="Letter of Engagement" filename={`${p.name.replace(/[^\w]+/g, "_")}_engagement.pdf`} build={() => engagementDoc(p, est)} />
      <style>{`@media (max-width:640px){ .sign-grid{ grid-template-columns:1fr !important } }`}</style>
    </div>
  );
}
